import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Building2, Upload, Scale, MapPin, Landmark, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: "legal_name", label: "Legal Company Name", full: true },
  { key: "brand_name", label: "Brand Name" },
  { key: "country", label: "Country" },
  { key: "status", label: "Status" },
  { key: "default_currency", label: "Default Currency" },
  { key: "timezone", label: "Timezone" },
  { key: "website", label: "Website" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "whatsapp", label: "WhatsApp" },
];

// Internationally generic legal identity. Indonesia: registration_number = NIB, tax_id = NPWP.
const LEGAL_FIELDS = [
  { key: "registration_number", label: "Business Registration Number (NIB)" },
  { key: "registration_authority", label: "Registration Authority" },
  { key: "registration_date", label: "Registration Date", type: "date" },
  { key: "tax_id", label: "Tax ID / NPWP" },
  { key: "tax_registration_number", label: "Tax Registration Number" },
  { key: "license_number", label: "Business License Number" },
  { key: "license_type", label: "License Type" },
  { key: "issuing_authority", label: "Issuing Authority" },
  { key: "issue_date", label: "Issue Date", type: "date" },
  { key: "expiry_date", label: "Expiry Date", type: "date" },
];

const ADDRESS_FIELDS = [
  { key: "address_line", label: "Address Line", full: true },
  { key: "city", label: "City" },
  { key: "state_region", label: "State / Region" },
  { key: "postal_code", label: "Postal Code" },
  { key: "country_iso", label: "Country (ISO code)", placeholder: "e.g. ID" },
];

const INPUT = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:focus:border-ink-gold dark:focus:ring-ink-gold disabled:bg-slate-50 disabled:text-slate-500 dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const LABEL = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400";

const clean = (obj, keys) => {
  const o = {};
  keys.forEach((k) => { if (obj?.[k]) o[k] = obj[k]; });
  return Object.keys(o).length ? o : undefined;
};

export default function CompanyProfile() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("company.edit");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/companies/me").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (obj) => (k, v) => setForm((f) => ({ ...f, [obj]: { ...(f[obj] || {}), [k]: v } }));
  const setLegal = setNested("legal_metadata");
  const setAddr = setNested("address_structured");

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => (payload[f.key] = form[f.key] ?? ""));
      payload.address = form.address ?? "";
      payload.branding = form.branding;
      const legal = clean(form.legal_metadata, LEGAL_FIELDS.map((f) => f.key));
      const addr = clean(form.address_structured, ADDRESS_FIELDS.map((f) => f.key));
      if (legal) payload.legal_metadata = legal;
      if (addr) payload.address_structured = addr;
      const { data } = await api.patch("/companies/me", payload);
      setForm(data);
      toast.success("Company profile updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/companies/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.logo_url, logo_object_key: data.logo_object_key }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  if (!form) return null;

  const logoSrc = form.logo_url ? (form.logo_url.startsWith("http") ? form.logo_url : BACKEND + form.logo_url) : null;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="company-profile-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Company Foundation</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Company Profile</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Core identity, legal registration and contact details. Edits are versioned and audited.</p>
        </div>
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid="company-save-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
        )}
      </div>

      {/* SECTION 1 — COMPANY IDENTITY */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="section-identity">
        <div className="mb-5 flex items-center gap-4 border-b border-slate-100 pb-5 dark:border-ink-line">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-ink-line dark:bg-ink-panel">
            {logoSrc ? <img src={logoSrc} alt="logo" className="h-full w-full object-contain" data-testid="company-logo-preview" /> : <Building2 className="h-7 w-7 text-slate-400" />}
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-slate-900 dark:text-ink-text">{form.brand_name}</div>
            <div className="text-xs text-slate-500">{form.legal_name}</div>
          </div>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-button"
                className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-ink-line dark:text-slate-200 dark:hover:bg-ink-panel">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload logo
              </button>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
              <label className={LABEL}>{f.label}</label>
              <input value={form[f.key] ?? ""} disabled={!canEdit} onChange={(e) => setField(f.key, e.target.value)} data-testid={`company-field-${f.key}`} className={INPUT} />
            </div>
          ))}
        </div>

        <div className="mt-6 border-t border-slate-100 pt-5 dark:border-ink-line">
          <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-slate-400">Brand Colors</label>
          <div className="flex gap-4">
            {["primary_color", "accent_color"].map((ck) => (
              <div key={ck} className="flex items-center gap-2">
                <input type="color" disabled={!canEdit} value={form.branding?.[ck] || "#0284C7"}
                  onChange={(e) => setForm((f) => ({ ...f, branding: { ...f.branding, [ck]: e.target.value } }))} data-testid={`company-color-${ck}`}
                  className="h-9 w-9 cursor-pointer rounded border border-slate-200 dark:border-ink-line" />
                <span className="text-xs text-slate-500">{ck.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SECTION 2 — LEGAL & REGULATORY */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="section-legal">
        <div className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-ink-line">
          <Scale className="h-4 w-4 text-sky-600 dark:text-ink-gold" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Legal &amp; Regulatory</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {LEGAL_FIELDS.map((f) => (
            <div key={f.key}>
              <label className={LABEL}>{f.label}</label>
              <input type={f.type || "text"} value={form.legal_metadata?.[f.key] ?? ""} disabled={!canEdit}
                onChange={(e) => setLegal(f.key, e.target.value)} data-testid={`legal-field-${f.key}`} className={INPUT} />
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 3 — BUSINESS ADDRESS */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="section-address">
        <div className="mb-4 flex items-center gap-2 border-b border-slate-100 pb-3 dark:border-ink-line">
          <MapPin className="h-4 w-4 text-sky-600 dark:text-ink-gold" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Business Address</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={LABEL}>Address (display)</label>
            <input value={form.address ?? ""} disabled={!canEdit} onChange={(e) => setField("address", e.target.value)} data-testid="company-field-address" className={INPUT} />
          </div>
          {ADDRESS_FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
              <label className={LABEL}>{f.label}</label>
              <input value={form.address_structured?.[f.key] ?? ""} disabled={!canEdit} placeholder={f.placeholder || ""}
                onChange={(e) => setAddr(f.key, e.target.value)} data-testid={`address-field-${f.key}`} className={INPUT} />
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 4 — BANK ACCOUNTS */}
      <BankAccountsSection />
    </div>
  );
}

const BANK_FIELDS = [
  { key: "bank_name", label: "Bank Name", req: true },
  { key: "branch_address", label: "Branch Address" },
  { key: "account_name", label: "Account Name", req: true },
  { key: "authorized_name", label: "Authorized Name" },
  { key: "account_number", label: "Account Number", req: true },
  { key: "swift_bic", label: "SWIFT / BIC" },
];
const BANK_CURRENCIES = ["IDR", "USD", "EUR", "AUD", "SGD", "CNY", "JPY", "GBP"];
const BANK_PURPOSES = ["B2B", "B2C", "Operational", "Other"];

function BankAccountsSection() {
  const { hasPermission } = useAuth();
  const canView = hasPermission("bank_account.view");
  const canCreate = hasPermission("bank_account.create");
  const canEdit = hasPermission("bank_account.edit");
  const [accounts, setAccounts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    api.get("/bank-accounts").then((r) => setAccounts(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [canView]);

  const upsert = (acc, draftId) => {
    setAccounts((list) => {
      const i = list.findIndex((a) => a.id === acc.id);
      return i >= 0 ? list.map((a) => (a.id === acc.id ? acc : a)) : [...list, acc];
    });
    if (draftId) setDrafts((d) => d.filter((x) => x !== draftId));
  };

  if (!canView) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-ink-line dark:bg-ink-card" data-testid="section-bank-accounts">
      <div className="mb-4 flex items-center justify-between gap-2 border-b border-slate-100 pb-3 dark:border-ink-line">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-emerald-600 dark:text-ink-gold" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-ink-text">Bank Accounts</h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 dark:bg-ink-panel" data-testid="bank-account-count">{accounts.length}</span>
        </div>
        {canCreate && (
          <button onClick={() => setDrafts((d) => [...d, `new-${Date.now()}`])} data-testid="bank-add-button"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300 dark:hover:bg-ink-panel">
            <Plus className="h-4 w-4" /> Add Bank Account
          </button>
        )}
      </div>
      {loading ? (
        <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : accounts.length === 0 && drafts.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="bank-accounts-empty">No bank accounts yet. Add your organization's receiving accounts here — each account is a separate, independent record.</p>
      ) : (
        <div className="space-y-4">
          {accounts.map((a, i) => (
            <BankAccountCard key={a.id} account={a} index={i} canEdit={canEdit} onSaved={upsert} />
          ))}
          {drafts.map((d, i) => (
            <BankAccountCard key={d} draftId={d} index={accounts.length + i} canEdit={canCreate} isNew
              onSaved={upsert} onDiscard={() => setDrafts((x) => x.filter((y) => y !== d))} />
          ))}
        </div>
      )}
    </div>
  );
}

function BankAccountCard({ account, draftId, index, isNew, canEdit, onSaved, onDiscard }) {
  const empty = { bank_name: "", branch_address: "", account_name: "", authorized_name: "", account_number: "", currency: "IDR", swift_bic: "", purpose: "Operational" };
  const [form, setForm] = useState(() => isNew ? empty : { ...empty, ...account, account_number: account.account_number || account.account_number_masked || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const idKey = account?.id || draftId;

  const save = async () => {
    if (!form.bank_name?.trim() || !form.account_name?.trim() || !form.account_number?.trim())
      return toast.error("Bank name, account name and account number are required");
    setSaving(true);
    try {
      const payload = {
        bank_name: form.bank_name.trim(), branch_address: form.branch_address || undefined,
        account_name: form.account_name.trim(), authorized_name: form.authorized_name || undefined,
        account_number: form.account_number.trim(), currency: form.currency,
        swift_bic: form.swift_bic || undefined, purpose: form.purpose,
      };
      const r = isNew ? await api.post("/bank-accounts", payload) : await api.patch(`/bank-accounts/${account.id}`, payload);
      toast.success(isNew ? "Bank account added" : "Bank account updated");
      onSaved({ ...r.data, account_number_masked: r.data.account_number_masked || `•••• ${String(r.data.account_number || "").slice(-4)}` }, draftId);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/bank-accounts/${account.id}`, { status: account.status === "active" ? "inactive" : "active" });
      toast.success(`Account ${r.data.status === "active" ? "activated" : "deactivated"}`);
      onSaved(r.data, null);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const inactive = !isNew && account.status === "inactive";
  return (
    <div className={`rounded-lg border p-4 ${inactive ? "border-slate-200 bg-slate-50/60 opacity-80 dark:border-ink-line dark:bg-ink-panel/30" : "border-slate-200 dark:border-ink-line"}`} data-testid={`bank-account-card-${idKey}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Bank Account {index + 1}</span>
        {!isNew && (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${inactive ? "bg-slate-200 text-slate-500 dark:bg-ink-panel" : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"}`} data-testid={`bank-status-${idKey}`}>{account.status}</span>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {BANK_FIELDS.map((f) => (
          <div key={f.key}>
            <label className={LABEL}>{f.label}{f.req && " *"}</label>
            <input value={form[f.key] ?? ""} disabled={!canEdit} onChange={set(f.key)} data-testid={`bank-${idKey}-${f.key}`} className={INPUT} />
          </div>
        ))}
        <div>
          <label className={LABEL}>Currency</label>
          <select value={form.currency} disabled={!canEdit} onChange={set("currency")} data-testid={`bank-${idKey}-currency`} className={INPUT}>
            {BANK_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL}>Purpose</label>
          <select value={form.purpose} disabled={!canEdit} onChange={set("purpose")} data-testid={`bank-${idKey}-purpose`} className={INPUT}>
            {BANK_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        {isNew && <button onClick={onDiscard} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-50 dark:border-ink-line dark:text-slate-400" data-testid={`bank-discard-${idKey}`}>Discard</button>}
        {!isNew && canEdit && (
          <button onClick={toggle} disabled={saving} data-testid={`bank-toggle-${idKey}`}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${inactive ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30" : "border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-500/30"}`}>
            {inactive ? "Activate" : "Deactivate"}
          </button>
        )}
        {canEdit && (
          <button onClick={save} disabled={saving} data-testid={`bank-save-${idKey}`}
            className="flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120]">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Account
          </button>
        )}
      </div>
    </div>
  );
}
